import BackToCPQButton from "../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../Components/PreviousPageButtonCPQ";

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import apiClient from "../Helpers/apiClient";

function FreightCompanyTable() {
    const [freightCompanies, setFreightCompanies] = useState([]);
    const [filteredFreightCompanies, setFilteredFreightCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [formData, setFormData] = useState({
        id: 0,
        companyName: "",
        website: "",
        notes: "",
        consignmentCharge: 0,
        fuelSurcharge: 0,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    // Sorting states
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    // Filtering states
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const navigate = useNavigate();

    useEffect(() => {
        fetchFreightCompanies();
    }, []);

    // Apply filters whenever freightCompanies or filter settings change
    useEffect(() => {
        applyFilters();
    }, [freightCompanies, filterText, filterField]);

    const fetchFreightCompanies = async () => {
        setLoading(true);
        try {
            console.log("Fetching freight companies...");

            const response = await apiClient.get("/api/FreightSystem/FreightCompanies");

            console.log("Response received successfully!");
            console.log("Data type:", typeof response.data);
            console.log("Is array:", Array.isArray(response.data));

            if (Array.isArray(response.data)) {
                setFreightCompanies(response.data);
                console.log(`Successfully loaded ${response.data.length} freight companies`);
            } else {
                console.warn("Response is not an array:", response.data);
                setFreightCompanies([]);
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching freight companies:", err);
            setError(`Failed to load data: ${err.message}`);
            setFreightCompanies([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (company = null) => {
        if (company) {
            setFormData({
                id: company.id || company.Id || 0,
                companyName: company.companyName || company.CompanyName || "",
                website: company.website || company.Website || "",
                notes: company.notes || company.Notes || "",
                consignmentCharge: company.consignmentCharge || company.ConsignmentCharge || 0,
                fuelSurcharge: company.fuelSurcharge || company.FuelSurcharge || 0,
            });
            setSelectedCompany(company);
        } else {
            setFormData({
                id: 0,
                companyName: "",
                website: "",
                notes: "",
                consignmentCharge: 0,
                fuelSurcharge: 0,
            });
            setSelectedCompany(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = (company) => {
        setSelectedCompany(company);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedCompany(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === "consignmentCharge" || name === "fuelSurcharge" ? parseFloat(value) || 0 : value,
        });
    };

    const handleSubmit = async () => {
        try {
            // Convert camelCase to PascalCase to match C# model property names
            const payload = {
                Id: formData.id,
                CompanyName: formData.companyName,
                Website: formData.website,
                Notes: formData.notes,
                ConsignmentCharge: formData.consignmentCharge,
                FuelSurcharge: formData.fuelSurcharge,
                Contacts: [],
                JutsTransportRates: [],
            };

            let response;
            if (selectedCompany) {
                // Update existing record
                response = await apiClient.put(`/api/FreightSystem/FreightCompanies/${payload.Id}`, payload);
                setSnackbar({
                    open: true,
                    message: "Freight company updated successfully!",
                    severity: "success",
                });
            } else {
                // Create new record
                response = await apiClient.post("/api/FreightSystem/FreightCompanies", payload);
                setSnackbar({
                    open: true,
                    message: "Freight company created successfully!",
                    severity: "success",
                });
            }

            handleCloseDialog();
            fetchFreightCompanies();
        } catch (err) {
            console.error("Error saving freight company:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/FreightSystem/FreightCompanies/${selectedCompany.id || selectedCompany.Id}`
            );
            setSnackbar({
                open: true,
                message: "Freight company deleted successfully!",
                severity: "success",
            });
            handleCloseDeleteDialog();
            fetchFreightCompanies();
        } catch (err) {
            console.error("Error deleting freight company:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Sorting functionality
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];

        return [...data].sort((a, b) => {
            let valueA, valueB;

            // Handle different property names
            if (orderBy === "id") {
                valueA = parseInt(a.id || a.Id || 0);
                valueB = parseInt(b.id || b.Id || 0);
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "companyName") {
                valueA = a.companyName || a.CompanyName || "";
                valueB = b.companyName || b.CompanyName || "";
            } else if (orderBy === "website") {
                valueA = a.website || a.Website || "";
                valueB = b.website || b.Website || "";
            } else if (orderBy === "consignmentCharge") {
                valueA = parseFloat(a.consignmentCharge || a.ConsignmentCharge || 0);
                valueB = parseFloat(b.consignmentCharge || b.ConsignmentCharge || 0);
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "fuelSurcharge") {
                valueA = parseFloat(a.fuelSurcharge || a.FuelSurcharge || 0);
                valueB = parseFloat(b.fuelSurcharge || b.FuelSurcharge || 0);
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            // For string values
            if (typeof valueA !== "number") {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    };

    // Filtering functionality
    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleFilterFieldChange = (field) => {
        setFilterField(field);
    };

    const clearFilter = () => {
        setFilterText("");
        setFilterField("all");
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredFreightCompanies(freightCompanies);
            return;
        }

        const searchTerm = filterText.toLowerCase().trim();

        const filtered = freightCompanies.filter((company) => {
            // Handle both camelCase and PascalCase property names from API
            const companyName = company.companyName || company.CompanyName || "";
            const website = company.website || company.Website || "";
            const notes = company.notes || company.Notes || "";
            const consignmentCharge = company.consignmentCharge || company.ConsignmentCharge || 0;
            const fuelSurcharge = company.fuelSurcharge || company.FuelSurcharge || 0;

            if (filterField === "all") {
                return (
                    String(company.id || company.Id || "").includes(searchTerm) ||
                    companyName.toLowerCase().includes(searchTerm) ||
                    website.toLowerCase().includes(searchTerm) ||
                    notes.toLowerCase().includes(searchTerm) ||
                    String(consignmentCharge).includes(searchTerm) ||
                    String(fuelSurcharge).includes(searchTerm)
                );
            }

            // Filter by specific field
            if (filterField === "id") return String(company.id || company.Id || "").includes(searchTerm);
            if (filterField === "companyName") return companyName.toLowerCase().includes(searchTerm);
            if (filterField === "website") return website.toLowerCase().includes(searchTerm);
            if (filterField === "notes") return notes.toLowerCase().includes(searchTerm);
            if (filterField === "consignmentCharge") return String(consignmentCharge).includes(searchTerm);
            if (filterField === "fuelSurcharge") return String(fuelSurcharge).includes(searchTerm);

            return false;
        });

        setFilteredFreightCompanies(filtered);
    };

    // Prepare data for rendering
    const displayData = getSortedData(filteredFreightCompanies);

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Freight Company Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        alignItems: "center",
                    }}
                >
                    {/* Filter */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "60%",
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearFilter} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Filter by field">
                            <Box>
                                <IconButton
                                    color={filterField !== "all" ? "primary" : "default"}
                                    aria-label="Filter options"
                                    onClick={(e) => {
                                        e.currentTarget.nextElementSibling.style.display =
                                            e.currentTarget.nextElementSibling.style.display === "none"
                                                ? "flex"
                                                : "none";
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        zIndex: 10,
                                        bgcolor: "background.paper",
                                        boxShadow: 3,
                                        borderRadius: 1,
                                        p: 1,
                                        display: "none",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {[
                                        { id: "all", label: "All Fields" },
                                        { id: "id", label: "ID" },
                                        { id: "companyName", label: "Company Name" },
                                        { id: "website", label: "Website" },
                                        { id: "consignmentCharge", label: "Consignment Charge" },
                                        { id: "fuelSurcharge", label: "Fuel Surcharge" },
                                    ].map((option) => (
                                        <Button
                                            key={option.id}
                                            size="small"
                                            variant={filterField === option.id ? "contained" : "text"}
                                            onClick={() => {
                                                handleFilterFieldChange(option.id);
                                                // Hide the filter dropdown
                                                document.querySelector(
                                                    '[aria-label="Filter options"]'
                                                ).nextElementSibling.style.display = "none";
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Tooltip>
                        {filterField !== "all" && (
                            <Typography variant="caption" sx={{ ml: 1 }}>
                                Filtering by:{" "}
                                {filterField === "id"
                                    ? "ID"
                                    : filterField === "companyName"
                                    ? "Company Name"
                                    : filterField === "website"
                                    ? "Website"
                                    : filterField === "consignmentCharge"
                                    ? "Consignment Charge"
                                    : filterField === "fuelSurcharge"
                                    ? "Fuel Surcharge"
                                    : ""}
                            </Typography>
                        )}
                    </Box>

                    {/* Add button */}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Company
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                flex: 1,
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader aria-label="freight company table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "id"}
                                                direction={orderBy === "id" ? order : "asc"}
                                                onClick={() => handleRequestSort("id")}
                                            >
                                                <strong>ID</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "companyName"}
                                                direction={orderBy === "companyName" ? order : "asc"}
                                                onClick={() => handleRequestSort("companyName")}
                                            >
                                                <strong>Company Name</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "website"}
                                                direction={orderBy === "website" ? order : "asc"}
                                                onClick={() => handleRequestSort("website")}
                                            >
                                                <strong>Website</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "consignmentCharge"}
                                                direction={orderBy === "consignmentCharge" ? order : "asc"}
                                                onClick={() => handleRequestSort("consignmentCharge")}
                                            >
                                                <strong>Consignment Charge</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "fuelSurcharge"}
                                                direction={orderBy === "fuelSurcharge" ? order : "asc"}
                                                onClick={() => handleRequestSort("fuelSurcharge")}
                                            >
                                                <strong>Fuel Surcharge</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                {filterText
                                                    ? "No matches found. Try a different search."
                                                    : "No data available. Add your first freight company."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((company) => (
                                            <TableRow key={company.id || company.Id} hover>
                                                <TableCell>{company.id || company.Id}</TableCell>
                                                <TableCell>
                                                    {company.companyName || company.CompanyName}
                                                </TableCell>
                                                <TableCell>
                                                    {company.website || company.Website ? (
                                                        <a
                                                            href={company.website || company.Website}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: "primary.main" }}
                                                        >
                                                            {company.website || company.Website}
                                                        </a>
                                                    ) : (
                                                        ""
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    $
                                                    {typeof (
                                                        company.consignmentCharge || company.ConsignmentCharge
                                                    ) === "number"
                                                        ? (
                                                              company.consignmentCharge ||
                                                              company.ConsignmentCharge
                                                          ).toFixed(2)
                                                        : "0.00"}
                                                </TableCell>
                                                <TableCell>
                                                    {typeof (
                                                        company.fuelSurcharge || company.FuelSurcharge
                                                    ) === "number"
                                                        ? (
                                                              company.fuelSurcharge || company.FuelSurcharge
                                                          ).toFixed(2)
                                                        : "0.00"}{" "}
                                                    %
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(company)}
                                                        aria-label="edit"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(company)}
                                                        aria-label="delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${freightCompanies.length} items`
                                    : `Total: ${freightCompanies.length} items`}
                            </Typography>
                            {filterText && (
                                <Button size="small" startIcon={<ClearIcon />} onClick={clearFilter}>
                                    Clear Filter
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <BackToCPQButton />
                <PreviousPageButtonCPQ />
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedCompany ? "Edit Freight Company" : "Add New Freight Company"}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Company Name"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="e.g., DHL Express"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Website"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    helperText="e.g., https://www.dhl.com"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Consignment Charge"
                                    name="consignmentCharge"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.consignmentCharge}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Fuel Surcharge"
                                    name="fuelSurcharge"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.fuelSurcharge}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleFormChange}
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    helperText="Any additional notes or comments"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {selectedCompany ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this freight company? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default FreightCompanyTable;
